import pbjs from 'prebid.js';
import './prebidModules';
import { ThirdParty, PreBid, BordeauxMachineContext } from '@repo/shared-types';
import { getParam } from '@repo/utils';

const lrh = getParam('lrh');

export const prebid = pbjs as unknown as PreBid;

export const initialise = (
  context: Pick<
    BordeauxMachineContext,
    'liveIntentUserSyncEnabled' | 'config' | 'sommelierResponse' | 'thirdPartyApiConfig'
  >,
): void => {
  const { sellerID } = context.thirdPartyApiConfig[ThirdParty.GLOBAL];
  const { enabled: uid2Enabled } = context.thirdPartyApiConfig[ThirdParty.UID2];
  const { enabled: euidEnabled } = context.thirdPartyApiConfig[ThirdParty.EUID];
  const { liveIntentUserSyncEnabled } = context;

  const userSyncIds = [
    {
      name: 'unifiedId',
      params: {
        url: '//match.adsrvr.org/track/rid?ttd_pid=w52yoji&fmt=json',
      },
      storage: {
        type: 'cookie',
        name: 'pbjs-unifiedid',
        expires: 60,
      },
    },
    {
      name: 'identityLink',
      params: { pid: '12243', notUse3P: false },
      storage: { type: 'cookie', name: 'idl_env', expires: 15, refreshInSeconds: 1800 },
    },
    {
      name: 'pairId',
      params: { liveramp: { storageKey: '_lr_pairId' } },
    },
    ...(euidEnabled ? [{ name: 'euid', params: { euidCookie: '__euid' } }] : []),
    ...(lrh && uid2Enabled && !prebid.getUserIds().uid2
      ? [
          {
            name: 'uid2',
            params: {
              serverPublicKey:
                'UID2-X-P-MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAErrdS+bkwKS6LIcrT4BWG7CivsrOds0tGfY667NctMQNtutObZa3Od9Uw5It/GiQJxJ1qD1IO1sdFVqHw11srpA==',
              subscriptionId: 'x2HQwg9gkp',
              emailHash: lrh,
            },
          },
        ]
      : []),
    ...(liveIntentUserSyncEnabled
      ? [
          {
            name: 'liveIntentId',
            bidders: [
              'rubicon',
              'pubmatic',
              'ix',
              'ttd',
              'grid',
              'medianet',
              'triplelift',
              'sovrn',
              'openx',
            ],
            params: {
              distributorId: 'did-0043',
              requestedAttributesOverrides: {
                uid2: true,
                index: true,
                bidswitch: true,
                pubmatic: true,
                magnite: true,
                openx: true,
                sovrn: true,
                thetradedesk: true,
                triplelift: true,
              },
            },
            storage: {
              type: 'html5',
              name: '__tamLIResolveResult',
              expires: 1,
            },
          },
        ]
      : []),
  ];

  prebid.processQueue();

  prebid.que.push(() => {
    prebid.setConfig({
      paapi: {
        enabled: true,
        defaultForSlots: 1,
      },
    });

    prebid.enableAnalytics([
      {
        provider: 'atsAnalytics',
        options: { pid: '12243' },
      },
    ]);

    prebid.mergeConfig({
      mediaTypePriceGranularity: {
        banner: {
          buckets: [
            { max: 10, increment: 0.01 },
            { max: 20, increment: 0.05 },
            { max: 50, increment: 0.25 },
            { max: 75, increment: 1 },
          ],
        },
        video: {
          buckets: [{ precision: 2, max: 70, increment: 0.1 }],
        },
      },
      consentManagement: {
        usp: { cmpApi: 'iab', timeout: 3000 },
        gdpr: {
          cmpApi: 'iab',
          defaultGdprScope: true,
          timeout: 3000,
          rules: [
            {
              purpose: 'storage',
              enforcePurpose: true,
              enforceVendor: true,
              vendorExceptions: ['permutive', 'pubxai'],
            },
            {
              purpose: 'basicAds',
              enforcePurpose: true,
              enforceVendor: true,
              vendorExceptions: [],
            },
          ],
        },
        gpp: { cmpApi: 'iab', timeout: 3000 },
      },
      realTimeData: {
        auctionDelay: 500,
        dataProviders: [
          {
            name: 'permutive',
            waitForIt: true,
            params: { acBidders: ['ix', 'appnexus', 'rubicon', 'pubmatic'] },
          },
        ],
      },
      userSync: {
        auctionDelay: 300,
        syncEnabled: true,
        syncsPerBidder: 3,
        syncDelay: 1000,
        filterSettings: {
          iframe: { bidders: '*', filter: 'include' },
          image: { bidders: '*', filter: 'include' },
        },
        userIds: userSyncIds,
      },
      ...(sellerID && {
        schain: {
          validation: 'strict',
          config: {
            complete: 1,
            ver: '1.0',
            nodes: [{ asi: 'futureplc.com', sid: sellerID, hp: 1 }],
          },
        },
      }),
    });

    prebid.setBidderConfig({
      bidders: ['criteo'],
      config: {
        ortb2: {
          site: {
            publisher: {
              id: '6783',
            },
          },
        },
      },
    });

    prebid.bidderSettings = {
      ...prebid.bidderSettings,
      standard: {
        storageAllowed: true,
      },
    };
  });
};
